import React from 'react';
import { makeStyles } from '@material-ui/core';

import { useIntl } from 'ha/i18n';
import {
  Box,
  Button,
  Container,
  Grid,
  Hidden,
  Typography,
  units,
} from '@ha/dsl';
import Img from 'ha/ui/next/Img';
import LazyComponent from 'ha/components/LazyComponent';

const useStyles = makeStyles(theme => ({
  container: {
    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
    },
  },
  item: {
    padding: units.yx(0, 0.75),
    marginBottom: units.rem(2.5),

    '&:last-child': {
      marginBottom: 0,
    },

    [theme.breakpoints.up('md')]: {
      position: 'relative',
      marginBottom: 0,
    },
  },
  iconWrapper: {
    [theme.breakpoints.up('md')]: {
      justifyContent: 'start',
    },
  },
  icon: {
    marginBottom: units.rem(1.25),
    height: units.rem(8.125),
    maxHeight: units.rem(8.125),

    [theme.breakpoints.up('md')]: {
      marginLeft: units.rem(2),
    },
  },
  paragraphTitle: {
    fontSize: units.rem(1.125),
  },
  index: {
    fontSize: units.rem(1.125),
    fontWeight: 'bold',
    lineHeight: 1.24,
    color: theme.palette.secondary.dark,
    marginRight: units.rem(0.25),

    [theme.breakpoints.up('md')]: {
      fontSize: units.rem(2.375),
      position: 'absolute',
      left: 0,
      marginRight: 0,
      lineHeight: 1,
    },
  },
  title: {
    marginBottom: units.rem(1),
    [theme.breakpoints.up('md')]: {
      justifyContent: 'start',
      marginLeft: units.rem(2),
    },
  },
  textWrapper: {
    [theme.breakpoints.up('md')]: {
      justifyContent: 'start',
    },
  },
  text: {
    [theme.breakpoints.up('md')]: {
      textAlign: 'left',
      marginLeft: units.rem(2),
    },
  },
  ctaContainer: {
    marginTop: units.rem(5),
  },
}));

interface Props {
  title?: string;
  description?: string;
  vps: Array<{
    icon: string;
    title: string;
    text: string;
  }>;
  ctaUrl?: string;
  ctaButtonText: string;
}

export const HowItWorks: React.FC<Props> = ({
  title,
  description,
  vps,
  ctaUrl,
  ctaButtonText,
}) => {
  const classes = useStyles();
  const { T } = useIntl();

  return (
    <Container maxWidth="lg">
      {title && (
        <Box marginBottom={3}>
          <Typography.H1 component="h2" align="center">
            {title}
          </Typography.H1>
        </Box>
      )}

      {description && (
        <Grid container justify="center">
          <Box marginBottom={7} maxWidth={640}>
            <Typography.Paragraph color="muted" align="center">
              {description}
            </Typography.Paragraph>
          </Box>
        </Grid>
      )}

      <Grid container justify="space-between" className={classes.container}>
        {vps.map((vp, index) => (
          <Grid
            container
            item
            key={vp.title}
            className={classes.item}
            direction="column"
          >
            <Grid
              item
              container
              justify="center"
              className={classes.iconWrapper}
            >
              <LazyComponent bottomOffset="-200px" scrollableAncestor="window">
                <Img
                  role="presentation"
                  className={classes.icon}
                  src={vp.icon}
                  data-test-locator={`Home HowItWorks Icon ${index}`}
                />
              </LazyComponent>
            </Grid>
            <Grid
              item
              container
              justify="center"
              className={classes.textWrapper}
            >
              <Grid
                item
                container
                justify="center"
                alignItems="baseline"
                className={classes.title}
              >
                <Typography.Paragraph className={classes.index}>
                  {index + 1}.
                </Typography.Paragraph>
                <Typography.ParagraphTitle
                  className={classes.paragraphTitle}
                  component="h3"
                  data-test-locator={`Home HowItWorks Title ${index}`}
                >
                  {T(vp.title)}
                </Typography.ParagraphTitle>
              </Grid>
              <Grid item>
                <Typography.Paragraph
                  align="center"
                  className={classes.text}
                  data-test-locator={`Home HowItWorks Text ${index}`}
                >
                  {T(vp.text)}
                </Typography.Paragraph>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>

      <Hidden smDown>
        <Grid container justify="center" className={classes.ctaContainer}>
          <Button
            variant="contained"
            color="primary"
            href={ctaUrl}
            data-test-locator="How It Works CTA"
          >
            {ctaButtonText}
          </Button>
        </Grid>
      </Hidden>
    </Container>
  );
};

export default HowItWorks;
